import { useCallback, useEffect, useRef, useState } from "react";
import { Logo } from "../dashboard/Logo";
import {
  isAppGroupToken,
  useAppChoiceGroups,
  useIntegrationsBySlug,
  type AppChoiceGroup,
  type CatalogIntegration,
  type CatalogPage,
} from "../../hooks/useIntegrationsCatalog";
import styles from "./IntegrationPicker.module.css";

// The catalog runs to ~1,000 entries, so the picker pages instead of loading
// the lot (that's ~660 KB of DTOs). One page keeps the dialog's first paint
// small; scrolling to the bottom pulls the next one, so everything is
// reachable without searching. 40 fills the 22rem list about three screens
// deep, which is enough that the first scroll already has somewhere to go.
const PAGE_SIZE = 40;

type Props = {
  selected: ReadonlyArray<string>;
  onChange: (slugs: ReadonlyArray<string>) => void;
  disabled?: boolean;
};

type PagedCatalog = {
  items: ReadonlyArray<CatalogIntegration>;
  loading: boolean;
  exhausted: boolean;
  loadMore: () => void;
};

// Append-as-you-scroll view of the catalog for one search term. Resets to
// page 1 whenever the term changes; `exhausted` goes true once the accumulated
// count reaches the server's reported total.
const usePagedCatalog = (query: string): PagedCatalog => {
  const [items, setItems] = useState<ReadonlyArray<CatalogIntegration>>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  // Guards against a scroll event firing another page request while one is
  // already in flight (the sentinel stays visible until the rows land).
  const inFlight = useRef<boolean>(false);

  useEffect(() => {
    setItems([]);
    setTotal(0);
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    const trimmed = query.trim();
    if (trimmed !== "") params.set("q", trimmed);
    params.set("page", String(page));
    params.set("pageSize", String(PAGE_SIZE));

    inFlight.current = true;
    setLoading(true);
    void fetch(`/api/public/integrations?${params.toString()}`, { credentials: "include" })
      .then((res: Response): Promise<CatalogPage> =>
        res.ok ? (res.json() as Promise<CatalogPage>) : Promise.reject(new Error(String(res.status))),
      )
      .then((body: CatalogPage): void => {
        if (cancelled) return;
        setTotal(body.total);
        // page 1 replaces (new search term), later pages append.
        setItems((prev) => (body.page <= 1 ? body.items : [...prev, ...body.items]));
      })
      .catch((): void => {
        /* non-fatal: the list just stops growing */
      })
      .finally((): void => {
        if (cancelled) return;
        inFlight.current = false;
        setLoading(false);
      });

    return (): void => {
      cancelled = true;
    };
  }, [query, page]);

  const exhausted = items.length >= total;

  const loadMore = useCallback((): void => {
    if (inFlight.current) return;
    setPage((p) => p + 1);
  }, []);

  return { items, loading, exhausted, loadMore };
};

// Search-and-tick multi-select over the live catalog. Selection order is the
// admin's: it survives into the invite row and drives the order the customer
// sees the apps in during onboarding, so the most important one can go first.
export const IntegrationPicker = ({ selected, onChange, disabled = false }: Props): JSX.Element => {
  const [query, setQuery] = useState<string>("");
  const { items, loading, exhausted, loadMore } = usePagedCatalog(query);
  // "Any of these" options (e.g. Email) sit above the catalog: often we don't
  // know which product a customer uses, only the job it has to do.
  const { groups } = useAppChoiceGroups();
  const groupById = new Map(groups.map((g) => [g.id, g]));
  const matchingGroups = groups.filter(
    (g) =>
      g.members.length > 0 &&
      (query.trim() === "" || g.title.toLowerCase().includes(query.trim().toLowerCase())),
  );
  // The selected slugs may sit outside the pages loaded so far, so their chips
  // resolve through a separate by-slug lookup.
  const { bySlug: selectedBySlug } = useIntegrationsBySlug(
    selected.filter((token) => !isAppGroupToken(token)),
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (node === null || exhausted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      // Start the next fetch a little before the sentinel is on screen so the
      // rows are usually there by the time the user reaches them.
      { root: node.parentElement, rootMargin: "120px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [exhausted, loadMore]);

  const toggle = (slug: string): void => {
    onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };

  return (
    <div className={styles.picker}>
      <div className={styles.selected}>
        {selected.length === 0 ? (
          <span className={styles.emptySelection}>No apps pinned yet.</span>
        ) : (
          selected.map((token) => {
            const group = groupById.get(token);
            const entry = selectedBySlug.get(token);
            const label = group?.title ?? entry?.title ?? token;
            return (
              <span key={token} className={styles.chip}>
                {group === undefined ? (
                  <Logo
                    slug={token}
                    size={16}
                    brandHex={entry?.iconBrandHex ?? null}
                    logoUrl={entry?.ourLogoUrl ?? entry?.logoUrl ?? null}
                  />
                ) : (
                  <span className={styles.groupMark} aria-hidden="true">✉</span>
                )}
                {label}
                {group !== undefined ? <span className={styles.groupHint}>any provider</span> : null}
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => toggle(token)}
                  disabled={disabled}
                  aria-label={`Remove ${label}`}
                >
                  ×
                </button>
              </span>
            );
          })
        )}
      </div>

      <input
        type="search"
        className={styles.search}
        value={query}
        placeholder="Search integrations…"
        aria-label="Search integrations"
        disabled={disabled}
        // The picker lives inside the dialog's <form>, and a text-like input
        // there implicitly submits on Enter — so filtering the list with the
        // most natural keystroke would fire "Send invite"/"Save apps" over
        // whatever happened to be ticked. The box filters as you type and has
        // nothing of its own to submit, so it swallows the key.
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className={styles.results} role="listbox" aria-label="Integrations">
        {items.length === 0 && loading && <div className={styles.empty}>Loading…</div>}
        {items.length === 0 && !loading && (
          <div className={styles.empty}>No integrations match “{query}”.</div>
        )}
        {matchingGroups.map((group: AppChoiceGroup) => {
          const on = selected.includes(group.id);
          return (
            <button
              key={group.id}
              type="button"
              role="option"
              aria-selected={on}
              aria-label={group.title}
              className={on ? `${styles.result} ${styles.resultOn}` : styles.result}
              disabled={disabled}
              onClick={() => toggle(group.id)}
            >
              <span className={styles.groupMark} aria-hidden="true">✉</span>
              {group.title}
              <span className={styles.resultMeta}>
                {on ? "Pinned" : `any of ${group.members.map((m) => m.title).join(", ")}`}
              </span>
            </button>
          );
        })}
        {items.map((item) => {
          const on = selected.includes(item.slug);
          return (
            <button
              key={item.slug}
              type="button"
              role="option"
              aria-selected={on}
              // Explicit label: the row also renders a role="img" logo and a
              // category, which would otherwise bury the title in the middle
              // of the computed accessible name.
              aria-label={item.title}
              className={on ? `${styles.result} ${styles.resultOn}` : styles.result}
              disabled={disabled}
              onClick={() => toggle(item.slug)}
            >
              <Logo
                slug={item.slug}
                size={18}
                brandHex={item.iconBrandHex}
                logoUrl={item.ourLogoUrl ?? item.logoUrl}
              />
              {item.title}
              <span className={styles.resultMeta}>{on ? "Pinned" : item.category}</span>
            </button>
          );
        })}
        {items.length > 0 && !exhausted && (
          <div className={styles.more} ref={sentinelRef}>
            Loading more…
          </div>
        )}
      </div>

      <p className={styles.hint}>
        Pinned apps show up as “connect these next” at the end of the customer&apos;s onboarding,
        each linking straight to its setup page.
      </p>
    </div>
  );
};
